import React, { useState, useEffect, useRef } from 'react';

export interface EnrolledCourse {
  course_id: string;
  name: string;
  title: string;
  status: string;
}

interface Props {
  courses: EnrolledCourse[];
  selectedCourseId?: string;
  onCourseSelect: (course: EnrolledCourse) => void;
}

const C = {
  navy: '#0e225d', navyMid: '#1a3370', gold: '#fbb21f',
  white: '#ffffff', border: '#e5e7eb', muted: '#9ca3af',
};

const SCROLL_STEP = 260;

// Hides the native scrollbar while keeping the strip scrollable
// (mouse wheel, touch and the arrow buttons still work).
const scrollbarHideStyles = `
  .course-strip-no-scrollbar { scrollbar-width: none; -ms-overflow-style: none; }
  .course-strip-no-scrollbar::-webkit-scrollbar { display: none; }
`;

const arrowButtonStyle = (disabled: boolean): React.CSSProperties => ({
  flexShrink: 0, width: '30px', height: '30px', borderRadius: '50%',
  border: `1px solid ${C.border}`, background: C.white,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.35 : 1,
  boxShadow: '0 1px 3px rgba(14,34,93,0.1)', color: C.navy,
  transition: 'opacity 0.15s ease',
});

const EnrolledCourseChips: React.FC<Props> = ({ courses, selectedCourseId, onCourseSelect }) => {
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const updateScrollButtons = () => {
    const el = scrollRef.current;
    if (!el) { setCanScrollLeft(false); setCanScrollRight(false); return; }
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  };

  useEffect(() => {
    updateScrollButtons();
    window.addEventListener('resize', updateScrollButtons);
    return () => window.removeEventListener('resize', updateScrollButtons);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courses]);

  const scrollByStep = (direction: 'left' | 'right') => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: direction === 'left' ? -SCROLL_STEP : SCROLL_STEP, behavior: 'smooth' });
    window.setTimeout(updateScrollButtons, 300);
  };

  if (courses.length === 0) {
    return (
      <div style={{ padding: '32px 20px', textAlign: 'center', background: 'rgba(14,34,93,0.03)', borderRadius: '12px', border: '2px dashed #E5E7EB' }}>
        <p style={{ margin: 0, color: C.muted, fontSize: '0.88rem' }}>You are not enrolled in any course yet</p>
      </div>
    );
  }

  return (
    <div style={{ width: '100%', boxSizing: 'border-box' }}>
      <style>{scrollbarHideStyles}</style>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <button
          type="button"
          aria-label="Scroll courses left"
          onClick={() => scrollByStep('left')}
          disabled={!canScrollLeft}
          style={arrowButtonStyle(!canScrollLeft)}
        >
          <i className="pi pi-chevron-left" style={{ fontSize: '0.75rem' }} />
        </button>

        <div
          ref={scrollRef}
          onScroll={updateScrollButtons}
          className="course-strip-no-scrollbar"
          style={{
            display: 'flex', flexWrap: 'nowrap', overflowX: 'auto', overflowY: 'hidden',
            gap: '8px', paddingBottom: '6px', paddingRight: '4px',
            WebkitOverflowScrolling: 'touch', flex: 1, minWidth: 0,
          }}
        >
          {courses.map((course) => {
            const isSelected = selectedCourseId === course.course_id;
            return (
              <div
                key={course.course_id}
                onClick={() => onCourseSelect(course)}
                title={course.title || course.name}
                style={{
                  padding: '8px 12px',
                  background: isSelected ? `linear-gradient(135deg, ${C.navy} 0%, ${C.navyMid} 100%)` : C.white,
                  border: isSelected ? '1.5px solid transparent' : `1.5px solid ${C.border}`,
                  borderRadius: '10px', cursor: 'pointer',
                  transition: 'background 0.25s ease, border-color 0.25s ease, box-shadow 0.25s ease, transform 0.15s ease',
                  transform: isSelected ? 'scale(1.02)' : 'scale(1)',
                  display: 'flex', alignItems: 'center', gap: '8px',
                  flex: '0 0 220px',
                  boxShadow: isSelected ? '0 3px 10px rgba(14,34,93,0.25)' : 'none',
                }}
              >
                <div
                  style={{
                    width: '24px', height: '24px', borderRadius: '50%',
                    background: isSelected ? C.gold : 'rgba(14,34,93,0.08)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    transition: 'background 0.25s ease',
                  }}
                >
                  <i className="pi pi-book" style={{ fontSize: '0.62rem', color: C.navy }} />
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '1px', minWidth: 0, flex: 1 }}>
                  <span style={{ fontSize: '0.68rem', fontWeight: 600, color: isSelected ? 'white' : C.navy, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', transition: 'color 0.25s ease' }}>
                    {course.name}
                  </span>
                  <span style={{ fontSize: '0.58rem', color: isSelected ? 'rgba(255,255,255,0.65)' : '#616366', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', transition: 'color 0.25s ease' }}>
                    {course.title}
                  </span>
                </div>

                {isSelected && <i className="pi pi-check" style={{ fontSize: '0.68rem', color: C.gold, flexShrink: 0 }} />}
              </div>
            );
          })}
        </div>

        <button
          type="button"
          aria-label="Scroll courses right"
          onClick={() => scrollByStep('right')}
          disabled={!canScrollRight}
          style={arrowButtonStyle(!canScrollRight)}
        >
          <i className="pi pi-chevron-right" style={{ fontSize: '0.75rem' }} />
        </button>
      </div>
    </div>
  );
};

export default EnrolledCourseChips;